<template>
  <div
    class="flex flex-col py-6 md:py-16 w-screen overflow-visible"
    :class="[
      twMerge(content?.background === 'dark' && 'bg-teal-900 text-stone-50'),
    ]"
  >
    <div class="w-screen md:max-w-screen-xl m-auto px-2">
      <h2
        :class="[
          twMerge(content?.background === 'dark' && 'text-stone-50'),
          content?.subtitle ? 'mb-2' : '',
        ]"
      >
        {{ content?.title }}
      </h2>
      <p v-if="content?.subtitle" class="text-h5 font-bold text-stone-800">
        {{ content?.subtitle }}
      </p>
    </div>

    <div class="full-bleed">
      <div
        ref="maskEl"
        class="slider-mask select-none"
        :class="{ 'is-dragging': dragging }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @pointerleave="onPointerUp"
      >
        <div ref="trackEl" class="slider-track will-change-transform">
          <div
            v-for="copyIndex in copiesCount"
            :key="'copy-' + copyIndex"
            class="slider-copy flex"
          >
            <template v-for="item in items" :key="`${copyIndex}-${item?.link}`">
              <NuxtLink
                :to="item?.link"
                class="p-3 mx-4 rounded-xl border border-gray-200 flex h-[120px] !w-fit"
              >
                <NuxtImg
                  v-if="item?.image?.url"
                  :src="String(item?.image?.url)"
                  :alt="item?.image?.alternativeText"
                  class="carousel-image w-[100px]"
                  draggable="false"
                />
                <div
                  v-if="item?.title"
                  class="p-3 flex flex-col justify-center"
                >
                  <div class="text-base mb whitespace-nowrap">
                    {{ item?.title }}
                  </div>
                  <span v-if="item?.description" class="text-sm">{{
                    item?.description
                  }}</span>
                </div>
              </NuxtLink>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge'

const { content = {} } = defineProps<{ content: any }>()

const sourceItems = computed(() => {
  if (!content?.items?.length) return []
  return [...content.items]
})

const items = ref<unknown[]>([])

watch(
  sourceItems,
  (list) => {
    items.value = [...list]
  },
  { immediate: true }
)

const copiesCount = computed(() => (items.value.length < 12 ? 3 : 2))

function shuffledCopy<T>(input: T[]): T[] {
  const a = [...input]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const maskEl = ref<HTMLElement | null>(null)
const trackEl = ref<HTMLElement | null>(null)

/** Auto-scroll: left to right (content moves left) */
const AUTO_SCROLL_SPEED = 0.03 // px/ms
let rafId: number | null = null
let lastTs = 0
let offset = 0
let singleTrackWidth = 0

/** Drag */
const dragging = ref(false)
let lastX = 0
let currentPointerId: number | null = null
let dragVelocitySamples: Array<{ dx: number; dt: number }> = []
let lastMoveTs = 0
let velocity = 0
let ro: ResizeObserver | null = null
const DRAG_SAMPLE_MAX = 6
const START_DRAG_THRESHOLD = 3
const FRICTION = 0.96
const MAX_VEL = 2.2
const MIN_VEL = 0.001

function measure() {
  const copy = trackEl.value?.querySelector(
    '.slider-copy'
  ) as HTMLElement | null
  singleTrackWidth = copy?.scrollWidth ?? 0
}

function applyTransform() {
  if (trackEl.value && singleTrackWidth) {
    trackEl.value.style.transform = `translate3d(${offset}px,0,0)`
  }
}

function normalizeOffset() {
  if (!singleTrackWidth) return
  if (offset <= -singleTrackWidth) offset += singleTrackWidth
  if (offset > 0) offset -= singleTrackWidth
}

function tick(ts: number) {
  if (!lastTs) lastTs = ts
  const dt = ts - lastTs
  lastTs = ts

  if (!dragging.value) {
    offset -= AUTO_SCROLL_SPEED * dt
    normalizeOffset()
  } else if (Math.abs(velocity) >= MIN_VEL) {
    offset += velocity * dt
    normalizeOffset()
    velocity *= FRICTION
    if (Math.abs(velocity) < MIN_VEL) velocity = 0
  }

  applyTransform()
  rafId = requestAnimationFrame(tick)
}

function startRaf() {
  if (rafId !== null) return
  lastTs = 0
  rafId = requestAnimationFrame(tick)
}

function stopRaf() {
  if (rafId !== null) cancelAnimationFrame(rafId)
  rafId = null
}

function onPointerDown(e: PointerEvent) {
  if (!maskEl.value) return
  currentPointerId = e.pointerId
  dragging.value = false
  lastX = e.clientX
  lastMoveTs = performance.now()
  dragVelocitySamples = []
}

function onPointerMove(e: PointerEvent) {
  if (!maskEl.value || currentPointerId === null) return

  const now = performance.now()
  const x = e.clientX
  const dx = x - lastX
  const dt = now - lastMoveTs || 1

  if (!dragging.value) {
    if (Math.abs(dx) > START_DRAG_THRESHOLD) {
      dragging.value = true
      maskEl.value.setPointerCapture(currentPointerId)
    } else {
      lastX = x
      lastMoveTs = now
      return
    }
  }

  offset += dx
  normalizeOffset()
  applyTransform()

  dragVelocitySamples.push({ dx, dt })
  if (dragVelocitySamples.length > DRAG_SAMPLE_MAX) dragVelocitySamples.shift()
  lastX = x
  lastMoveTs = now
}

function onPointerUp() {
  if (maskEl.value && currentPointerId !== null) {
    try {
      maskEl.value.releasePointerCapture(currentPointerId)
    } catch {
      console.warn('Failed to release pointer capture')
    }
  }
  currentPointerId = null
  dragging.value = false

  if (dragVelocitySamples.length) {
    const sum = dragVelocitySamples.reduce(
      (acc, s) => ({ dx: acc.dx + s.dx, dt: acc.dt + s.dt }),
      { dx: 0, dt: 0 }
    )
    velocity = sum.dx / Math.max(sum.dt, 1)
    velocity = Math.max(Math.min(velocity, MAX_VEL), -MAX_VEL)
  }
}

onMounted(async () => {
  if (items.value.length > 1) {
    items.value = shuffledCopy(items.value)
  }
  await nextTick()
  requestAnimationFrame(measure)

  maskEl.value?.querySelectorAll('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', measure, { once: true })
  })

  ro = new ResizeObserver(measure)
  if (trackEl.value) ro.observe(trackEl.value)

  startRaf()
})

onUnmounted(() => {
  stopRaf()
  ro?.disconnect()
})

watch(items, () => {
  offset = 0
  applyTransform()
  measure()
})
</script>

<style lang="postcss" scoped>
.full-bleed {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.slider-mask {
  overflow: hidden;
  padding-top: 1rem;
  cursor: grab;
  touch-action: pan-y;
}

.slider-mask.is-dragging {
  cursor: grabbing;
}

.slider-track {
  display: flex;
  flex-wrap: nowrap;
}

.slider-copy {
  flex-shrink: 0;
}

.carousel-image {
  min-width: 6rem;
  height: 100%;
  object-fit: cover;
  @apply rounded-lg;
}
</style>
